/**
 * Selection helpers shared by the payout run sub-tables and the bulk action bar
 * so row keys, amounts, and action targets stay identical in both places.
 */

const FINALIZED_CONTRACT_STATUSES = new Set(["approved", "paid", "declined"]);
const FINALIZED_BONUS_STATUSES = new Set(["approved", "paid", "declined"]);
const FINALIZED_CHARGEBACK_STATUSES = new Set([
  "approved",
  "paid",
  "declined",
]);

/** True when every commission and bonus in the group has been approved or paid. */
export function isAgentGroupFullyReviewed(agentGroup) {
  const records = [
    ...(agentGroup?.contracts ?? []),
    ...(agentGroup?.bonuses ?? []),
    ...(agentGroup?.chargebacks ?? []),
  ];

  if (records.length === 0) return false;

  return !records.some((record) => record?.status === "pending");
}

function sum(list, key) {
  return (list ?? []).reduce((acc, item) => acc + (item?.[key] ?? 0), 0);
}

export function getContractAmount(agentContract) {
  const records = agentContract?.commissionRecords ?? [];
  const baseAmount = sum(records, "amount");
  const adjustmentTotal = sum(
    agentContract?.contractAdjustments,
    "adjustmentAmount",
  );

  return baseAmount + adjustmentTotal;
}

export function getBonusAmount(bonus) {
  const adjustmentTotal = (bonus?.adjustments ?? []).reduce(
    (acc, a) => acc + (a.adjustmentAmount ?? a.adjustedAmount ?? 0),
    0,
  );

  return (bonus?.amount ?? 0) + adjustmentTotal;
}

export function getChargebackAmount(chargeback) {
  const adjustmentTotal = (chargeback?.adjustments ?? []).reduce(
    (acc, a) => acc + (a.adjustmentAmount ?? a.adjustedAmount ?? 0),
    0,
  );

  return (chargeback?.amount ?? 0) + adjustmentTotal;
}

export function getContractSelectionKey(agentKey, agentContract, index) {
  const contractNumber = agentContract?.commissionRecords?.[0]?.contractNumber;
  return `commission:${agentKey}:${
    agentContract?.commissionAgentContractId ?? contractNumber ?? index
  }`;
}

export function getBonusSelectionKey(agentKey, bonus, index) {
  return `bonus:${agentKey}:${bonus?.bonusRecordId ?? index}`;
}

export function isContractSelectable(agentContract) {
  return !FINALIZED_CONTRACT_STATUSES.has(agentContract?.status);
}

export function isBonusSelectable(bonus) {
  return !FINALIZED_BONUS_STATUSES.has(bonus?.status);
}

export function getChargebackSelectionKey(agentKey, chargeback, index) {
  return `chargeback:${agentKey}:${chargeback?.chargebackRecordId ?? index}`;
}

export function isChargebackSelectable(chargeback) {
  return !FINALIZED_CHARGEBACK_STATUSES.has(chargeback?.status);
}

export function buildChargebackSelectionRow(agentKey, chargeback, index) {
  return {
    key: getChargebackSelectionKey(agentKey, chargeback, index),
    kind: "chargeback",
    amount: getChargebackAmount(chargeback),
    selectable: isChargebackSelectable(chargeback),
    target: {
      kind: "chargeback",
      id: chargeback?.chargebackRecordId,
      label: chargeback?.chargebackType || "Chargeback",
    },
  };
}

export function buildContractSelectionRow(agentKey, agentContract, index) {
  const contractNumber = agentContract?.commissionRecords?.[0]?.contractNumber;
  const id = agentContract?.commissionAgentContractId;

  return {
    key: getContractSelectionKey(agentKey, agentContract, index),
    kind: "commission",
    amount: getContractAmount(agentContract),
    selectable: isContractSelectable(agentContract),
    target: {
      kind: "commission",
      id,
      agentId: agentContract?.agentId,
      contractNumber,
      label: `Contract #${contractNumber || id}`,
    },
  };
}

export function buildBonusSelectionRow(agentKey, bonus, index) {
  return {
    key: getBonusSelectionKey(agentKey, bonus, index),
    kind: "bonus",
    amount: getBonusAmount(bonus),
    selectable: isBonusSelectable(bonus),
    target: {
      kind: "bonus",
      id: bonus?.bonusRecordId,
      label: bonus?.bonusRuleName || "Bonus",
    },
  };
}

/** Flattens every selectable commission and bonus row across all agent groups. */
export function buildSelectionRows(agentGroups) {
  return (agentGroups ?? []).flatMap((group) => [
    ...(group.contracts ?? []).map((ac, i) =>
      buildContractSelectionRow(group.key, ac, i),
    ),
    ...(group.bonuses ?? []).map((bonus, i) =>
      buildBonusSelectionRow(group.key, bonus, i),
    ),
    ...(group.chargebacks ?? []).map((chargeback, i) =>
      buildChargebackSelectionRow(group.key, chargeback, i),
    ),
  ]);
}
